import { useMutation } from "@apollo/client";
import StickyNote2Outlined from "@mui/icons-material/StickyNote2Outlined";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  AddFulfillmentNoteDialog,
  FacilityRowData,
} from "components/AddFulfillmentNoteDialog";
import { gql } from "generated-graphql";
import { hasValue } from "encamp-shared/src/utils/hasValue";
import pluralize from "pluralize";
import { useCallback, useMemo } from "react";
import { useBulkActionContext } from ".";

const UPDATE_FULFILLMENT_NOTES_BULK = gql(`
  mutation UpdateFulfillmentNotesBulk($reportIds: [ID!]!, $note: String) {
    updateTierIIReportsFulfillmentNoteBulk(reportIds: $reportIds, note: $note)
  }
`);

export function AddFulfillmentNoteMenuItem() {
  const { setDialogState } = useBulkActionContext();

  return (
    <MenuItem onClick={() => setDialogState("addFulfillmentNote")}>
      <ListItemIcon>
        <StickyNote2Outlined />
      </ListItemIcon>
      <ListItemText>Add Fulfillment Note</ListItemText>
    </MenuItem>
  );
}

export function AddFulfillmentNoteDialog_() {
  const alerts = useAlerts();
  const { dialogState, onClose, selectedRows } = useBulkActionContext();

  const [updateNotes, { loading }] = useMutation(
    UPDATE_FULFILLMENT_NOTES_BULK,
    {
      refetchQueries: ["ChemicalFacilities"],
    }
  );

  const facilities: FacilityRowData[] = useMemo(
    () =>
      selectedRows
        .filter((row) => hasValue(row.tierIIReportId))
        .map((row) => ({
          reportId: row.tierIIReportId,
          organizationName: row.organization ?? "",
          facilityName: row.facilityName ?? "",
          facilityAddress: [row.streetAddress1, row.city, row.state]
            .filter(Boolean)
            .join(", "),
          facilityId: row.customerFacilityId ?? "",
          tenantId: row.organization || undefined,
        })),
    [selectedRows]
  );

  const reportIds = useMemo(
    () => selectedRows.map((row) => row.tierIIReportId).filter(hasValue),
    [selectedRows]
  );

  const handleSubmit = useCallback(
    async (note: string) => {
      try {
        const result = await updateNotes({
          variables: {
            reportIds,
            note: note || null,
          },
        });

        const updatedCount =
          result.data?.updateTierIIReportsFulfillmentNoteBulk ?? 0;
        alerts.success(
          `Successfully updated fulfillment note for ${updatedCount} ${pluralize(
            "report",
            updatedCount
          )}`
        );
        onClose();
      } catch (err) {
        alerts.error("An error occurred while updating fulfillment notes", err);
      }
    },
    [updateNotes, reportIds, alerts, onClose]
  );

  if (dialogState !== "addFulfillmentNote") {
    return null;
  }

  return (
    <AddFulfillmentNoteDialog
      open={true}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      facilities={facilities}
    />
  );
}
